/**
 * Graph Explorer - Modal Module
 * Uses native browser prompt/confirm dialogs for node actions
 * (Extend, Connect, Map, Isolate, Remove, Find, Search, etc.)
 */

(function(global) {
    'use strict';

    // Action configurations (used for prompt messages)
    const actionConfigs = {
        find: {
            title: 'Find',
            prompt: 'Enter node names (comma-separated):',
            needsInput: true
        },
        search: {
            title: 'Search',
            prompt: 'Enter search query:',
            needsInput: true
        },
        extend: {
            title: 'Extend',
            prompt: 'Extend the graph by finding connected nodes.\n\nEnter search filter or leave empty for NEIGHBORS:',
            needsInput: true,
            allowEmpty: true
        },
        connect: {
            title: 'Connect',
            prompt: 'Find paths and connections between selected nodes.\n\nEnter connection parameters or leave empty for WITHIN:',
            needsInput: true,
            allowEmpty: true
        },
        map: {
            title: 'Map',
            prompt: 'Map selected nodes to external resources or taxonomies.\n\nEnter mapping target:',
            needsInput: true
        },
        intersect: {
            title: 'Intersect',
            prompt: 'Find nodes connected to ALL selected nodes.\n\nEnter alias to intersect with:',
            needsInput: true
        },
        differs: {
            title: 'Differs',
            prompt: 'Find nodes that differ between selected nodes.\n\nEnter alias to compare with:',
            needsInput: true
        },
        union: {
            title: 'Union',
            prompt: 'Combine all connections from selected nodes.\n\nEnter alias to union with:',
            needsInput: true
        },
        isolate: {
            title: 'Isolate',
            confirm: 'Isolate the selected node(s) and their direct connections?\n\nAll other nodes will be removed from the view.',
            needsInput: false
        },
        remove: {
            title: 'Remove',
            confirm: 'Remove the selected node(s) from the current view?',
            needsInput: false
        }
    };

    // ============================================
    // Initialization
    // ============================================
    function init() {
        console.log('[ExplorerModal] Initialized (native dialogs)');
    }

    // ============================================
    // Modal Operations (native prompt/confirm)
    // ============================================
    function open(action, options) {
        const config = actionConfigs[action];

        // Handle algorithm/function actions passed with options
        if (!config && options) {
            handleSpecialAction(action, options);
            return;
        }

        if (!config) {
            console.warn('[ExplorerModal] Unknown action:', action);
            return;
        }

        // Hide hover menu if visible
        if (typeof ExplorerHoverMenu !== 'undefined') {
            ExplorerHoverMenu.hideMenu();
        }

        console.log('[ExplorerModal] Opening:', action);

        // Dispatch to appropriate handler
        switch (action) {
            case 'find':
                openFind();
                break;
            case 'search':
                openSearch();
                break;
            case 'isolate':
            case 'remove':
                openConfirmAction(action);
                break;
            default:
                openPromptAction(action);
                break;
        }
    }

    function close() {
        // No-op for native dialogs (they close themselves)
    }

    function isOpen() {
        return false;
    }

    // ============================================
    // Native Dialog Handlers
    // ============================================
    function openFind() {
        const input = prompt('Find — Enter node names (comma-separated):');
        if (input === null || !input.trim()) return;
        executeFindAction(input, 'node');
    }

    function openSearch() {
        const query = prompt('Search — Enter search query:');
        if (query === null || !query.trim()) return;
        performSearch(query, 'node');
    }

    function openConfirmAction(action) {
        const config = actionConfigs[action];

        // Check for selected nodes first
        const state = GraphExplorer.getState();
        let selectedNodes = state.selectedNodes || [];
        if (selectedNodes.length === 0 && typeof ExplorerWindows !== 'undefined' && ExplorerWindows.getSelectedNodes) {
            const selectedNodeData = ExplorerWindows.getSelectedNodes() || [];
            selectedNodes = selectedNodeData.map(node => node.id).filter(Boolean);
        }

        if (selectedNodes.length === 0) {
            alert('Please select nodes first');
            return;
        }

        const message = `${config.confirm}\n\n(${selectedNodes.length} node(s) selected)`;
        if (confirm(message)) {
            executeAction(action, '');
        }
    }

    function openPromptAction(action) {
        const config = actionConfigs[action];

        // Check for selected nodes first (find/search don't need selection)
        if (action !== 'find' && action !== 'search') {
            const state = GraphExplorer.getState();
            let selectedNodes = state.selectedNodes || [];
            if (selectedNodes.length === 0 && typeof ExplorerWindows !== 'undefined' && ExplorerWindows.getSelectedNodes) {
                const selectedNodeData = ExplorerWindows.getSelectedNodes() || [];
                selectedNodes = selectedNodeData.map(node => node.id).filter(Boolean);
            }

            if (selectedNodes.length === 0) {
                alert('Please select nodes first');
                return;
            }
        }

        const input = prompt(config.prompt);
        if (input === null) return;
        if (!config.allowEmpty && !input.trim()) return;

        executeAction(action, input);
    }

    // ============================================
    // Special Actions (algorithm, function)
    // ============================================
    function handleSpecialAction(action, options) {
        if (action === 'algorithm') {
            const algoName = options.algorithm || options.algoId || 'unknown';
            const input = prompt(`Run algorithm: ${algoName}\n\nEnter parameters (or leave empty for defaults):`);
            if (input === null) return;
            console.log('[ExplorerModal] Algorithm:', options.algoId, 'params:', input);
            ExplorerUtils.announceChange(`Running ${algoName} algorithm...`);
        } else if (action === 'function') {
            const fnName = options.name || options.functionId || 'unknown';
            const input = prompt(`Run function: ${fnName}\n\n${options.description || ''}\n\nEnter parameters (or leave empty for defaults):`);
            if (input === null) return;
            console.log('[ExplorerModal] Function:', options.functionId, 'params:', input);
            ExplorerUtils.announceChange(`Running function: ${fnName}...`);
        }
    }

    // ============================================
    // Search & Find Helpers
    // ============================================
    async function performSearch(query, entityType) {
        entityType = entityType || 'node';
        console.log('[ExplorerModal] Performing search:', entityType, query);

        try {
            // Build MQL query for search
            const mqlQuery = `SEARCH ${entityType.toUpperCase()}("${query}") LIMIT 20;`;
            const results = await ExplorerAPI.executeMQL(mqlQuery);

            if (results && results.items && results.items.length > 0) {
                // Add all results to graph
                const selectedIds = results.items.map(item => item.id);
                const idsStr = selectedIds.map(id => `"${id}"`).join(', ');
                const aliasName = `search_${Date.now()}`;
                const addQuery = `SEARCH ${entityType.toUpperCase()}("_") | ADD([${idsStr}]) AS ${aliasName};`;

                await ExplorerAPI.executeMQL(addQuery);
                await ExplorerAPI.refreshGraph();
                console.log('[ExplorerModal] Added', selectedIds.length, 'items to graph');
            } else {
                alert('No results found for: ' + query);
            }
        } catch (error) {
            console.error('[ExplorerModal] Search failed:', error);
            alert('Search failed: ' + error.message);
        }
    }

    async function executeFindAction(inputValue, entityType) {
        if (!inputValue.trim()) {
            alert('Please enter node names to find');
            return;
        }

        entityType = entityType || 'node';

        // Parse comma-separated values
        const names = inputValue.split(',').map(s => s.trim()).filter(s => s);

        if (names.length === 0) {
            alert('Please enter at least one name');
            return;
        }

        // Build MQL FIND query
        let mqlQuery;
        const aliasName = `find_${Date.now()}`;

        if (names.length === 1) {
            mqlQuery = `FIND ${entityType.toUpperCase()}("${names[0]}") AS ${aliasName};`;
        } else {
            const namesStr = names.map(n => `"${n}"`).join(', ');
            mqlQuery = `FIND ${entityType.toUpperCase()}([${namesStr}]) AS ${aliasName};`;
        }

        console.log('[ExplorerModal] Executing FIND:', mqlQuery);

        try {
            await ExplorerAPI.executeMQL(mqlQuery);
            await ExplorerAPI.refreshGraph();
            console.log('[ExplorerModal] FIND completed successfully');
        } catch (error) {
            console.error('[ExplorerModal] FIND failed:', error);
            alert('Find failed: ' + error.message);
        }
    }

    // ============================================
    // Action Execution
    // ============================================
    async function executeAction(action, inputValue) {
        // Get selected nodes and alias
        const state = GraphExplorer.getState();
        let selectedNodes = state.selectedNodes || [];
        const selectedAlias = state.selectedAlias;

        // Fallback: use Selection window state if GraphExplorer state is empty
        if (selectedNodes.length === 0 && typeof ExplorerWindows !== 'undefined' && ExplorerWindows.getSelectedNodes) {
            const selectedNodeData = ExplorerWindows.getSelectedNodes() || [];
            selectedNodes = selectedNodeData.map(node => node.id).filter(Boolean);
        }

        console.log('[ExplorerModal] Executing:', action, 'on nodes:', selectedNodes, 'with input:', inputValue, 'alias:', selectedAlias);

        if (selectedNodes.length === 0 && action !== 'find' && action !== 'search') {
            alert('Please select nodes first');
            return;
        }

        // Use SELECT ALIAS if available, otherwise SELECT NODE with IDs
        const selectClause = selectedAlias
            ? `SELECT ALIAS("${selectedAlias}")`
            : `SELECT NODE([${selectedNodes.map(id => `"${id}"`).join(', ')}])`;

        const aliasName = `${action}_${Date.now()}`;
        let mqlQuery = '';

        switch (action) {
            case 'find':
                executeFindAction(inputValue, 'node');
                return;
            case 'search':
                // Search is handled separately
                return;

            case 'extend':
                const extendTarget = inputValue || 'NEIGHBORS';
                mqlQuery = `${selectClause} | EXTEND TO ${extendTarget} AS ${aliasName};`;
                break;

            case 'connect':
                const connectType = inputValue || 'WITHIN';
                mqlQuery = `${selectClause} | CONNECT ${connectType} AS ${aliasName};`;
                break;

            case 'map':
                mqlQuery = `${selectClause} | MAP PATHS ${inputValue} AS ${aliasName};`;
                break;

            case 'intersect':
                if (!inputValue) {
                    alert('Please provide an alias to intersect with');
                    return;
                }
                mqlQuery = `${selectClause} | INTERSECT(${inputValue}) AS ${aliasName};`;
                break;

            case 'differs':
                if (!inputValue) {
                    alert('Please provide an alias to compare with');
                    return;
                }
                mqlQuery = `${selectClause} | DIFFERENCE(${inputValue}) AS ${aliasName};`;
                break;

            case 'union':
                if (!inputValue) {
                    alert('Please provide an alias to union with');
                    return;
                }
                mqlQuery = `${selectClause} | UNION(${inputValue}) AS ${aliasName};`;
                break;

            case 'isolate':
                mqlQuery = `${selectClause} | ISOLATE() AS ${aliasName};`;
                break;

            case 'remove':
                mqlQuery = `${selectClause} | REMOVE() AS removed_${Date.now()}; SELECT NODE() AS ${aliasName};`;
                break;
        }

        if (mqlQuery) {
            try {
                console.log('[ExplorerModal] Executing MQL:', mqlQuery);
                await ExplorerAPI.executeMQL(mqlQuery);
                const forceRefresh = ['remove', 'isolate'].includes(action);
                await ExplorerAPI.refreshGraph(forceRefresh);
                console.log('[ExplorerModal] Action completed successfully');
            } catch (error) {
                console.error('[ExplorerModal] Action failed:', error);
                alert('Action failed: ' + error.message);
            }
        }

        ExplorerUtils.announceChange(action + ' action executed');
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerModal = {
        init,
        open,
        close,
        isOpen
    };

})(window);
